import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { DeliveryNote } from '@/types/deliveryNote';
import { formatCurrency } from '@/types/deliveryNote';

// Color rojo corporativo de MAGMA
const MAGMA_RED = [180, 30, 30] as const;
const GRAY_LIGHT = [240, 240, 240] as const;
const GRAY_DARK = [100, 100, 100] as const;

export function generateDeliveryNotePdf(data: DeliveryNote): jsPDF {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const hideValues = data.hideValues === true;
  let yPos = margin;

  // ============================================
  // HEADER - Company Info (Left side)
  // ============================================
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(data.company.name, margin, yPos);
  yPos += 5;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY_DARK);
  doc.text(data.company.address, margin, yPos);
  yPos += 4;
  doc.text(`${data.company.postalCode}   ${data.company.city}`, margin, yPos);
  yPos += 4;
  doc.text(data.company.province, margin, yPos);
  yPos += 6;
  doc.setTextColor(0, 0, 0);
  doc.text(data.company.cif, margin, yPos);
  yPos += 4;
  doc.text(data.company.phone, margin, yPos);

  // ============================================
  // HEADER - Customer Info (Right side box)
  // ============================================
  const customerBoxX = pageWidth / 2 + 5;
  const customerBoxY = 20;
  const customerBoxWidth = pageWidth / 2 - margin - 5;
  const customerBoxHeight = 35;

  // Customer box background
  doc.setFillColor(...GRAY_LIGHT);
  doc.rect(customerBoxX, customerBoxY, customerBoxWidth, customerBoxHeight, 'F');

  // Customer info
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(data.customer.name, customerBoxX + 3, customerBoxY + 7);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(data.customer.cif, customerBoxX + 3, customerBoxY + 13);
  doc.text(data.customer.address, customerBoxX + 3, customerBoxY + 19);
  doc.text(`${data.customer.postalCode || ''} ${data.customer.city}`.trim(), customerBoxX + 3, customerBoxY + 25);
  doc.text(data.customer.province, customerBoxX + 3, customerBoxY + 31);

  yPos = 65;

  // ============================================
  // DOCUMENT INFO TABLE
  // ============================================
  const docInfoY = yPos;
  const colWidth = (pageWidth - margin * 2) / 4;

  // Header row
  doc.setFillColor(...GRAY_LIGHT);
  doc.rect(margin, docInfoY, pageWidth - margin * 2, 7, 'F');

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...GRAY_DARK);
  doc.text('Documento', margin + 3, docInfoY + 5);
  doc.text('Número', margin + colWidth + 3, docInfoY + 5);
  doc.text('Página', margin + colWidth * 2 + 3, docInfoY + 5);
  doc.text('Fecha', margin + colWidth * 3 + 3, docInfoY + 5);

  // Data row
  doc.setDrawColor(200, 200, 200);
  doc.rect(margin, docInfoY + 7, pageWidth - margin * 2, 8);

  // Draw vertical lines
  doc.line(margin + colWidth, docInfoY, margin + colWidth, docInfoY + 15);
  doc.line(margin + colWidth * 2, docInfoY, margin + colWidth * 2, docInfoY + 15);
  doc.line(margin + colWidth * 3, docInfoY, margin + colWidth * 3, docInfoY + 15);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.text(data.documentType, margin + 3, docInfoY + 13);
  doc.text(data.documentNumber, margin + colWidth + 3, docInfoY + 13);
  doc.text(`${data.page}`, margin + colWidth * 2 + 3, docInfoY + 13);
  doc.text(data.date, margin + colWidth * 3 + 3, docInfoY + 13);

  yPos = docInfoY + 22;

  // ============================================
  // PAYMENT INFO
  // ============================================
  doc.setFillColor(...GRAY_LIGHT);
  doc.rect(margin, yPos, 50, 6, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...GRAY_DARK);
  doc.text('Datos de pago', margin + 2, yPos + 4);

  if (!hideValues) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.text(data.paymentMethod, margin + 2, yPos + 11);
  }

  yPos += 20;

  // ============================================
  // ITEMS TABLE
  // ============================================
  const tableColumns = [
    { header: 'ARTÍCULO', dataKey: 'article' },
    { header: 'DESCRIPCIÓN', dataKey: 'description' },
    { header: 'CANTIDAD', dataKey: 'quantity' },
    { header: 'PRECIO UD.', dataKey: 'unitPrice' },
    { header: 'SUBTOTAL', dataKey: 'subtotal' },
    { header: 'DTO.', dataKey: 'discount' },
    { header: 'TOTAL', dataKey: 'total' },
  ];

  const tableData = data.items.map(item => ({
    article: item.article,
    description: item.description,
    quantity: formatCurrency(item.quantity),
    unitPrice: hideValues ? '' : formatCurrency(item.unitPrice),
    subtotal: formatCurrency(item.subtotal),
    discount: item.discount > 0 ? `${item.discount}%` : '',
    total: hideValues ? '' : formatCurrency(item.total),
  }));

  const tableBody = tableData.map(row =>
    tableColumns.map(col => row[col.dataKey as keyof typeof row])
  );

  autoTable(doc, {
    startY: yPos,
    head: [tableColumns.map(col => col.header)],
    body: tableBody,
    theme: 'plain',
    headStyles: {
      fillColor: MAGMA_RED,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8,
      halign: 'center',
      cellPadding: 3,
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 4,
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
    },
    columnStyles: {
      0: { cellWidth: 20, halign: 'center' },
      1: { cellWidth: 55 },
      2: { cellWidth: 22, halign: 'right' },
      3: { cellWidth: 22, halign: 'right' },
      4: { cellWidth: 25, halign: 'right' },
      5: { cellWidth: 15, halign: 'center' },
      6: { cellWidth: 25, halign: 'right' },
    },
    margin: { left: margin, right: margin },
    tableLineColor: [200, 200, 200],
    tableLineWidth: 0.1,
  });

  // Get Y position after items table
  yPos = (doc as any).lastAutoTable.finalY + 10;
  // Ensure minimum space for tax table (at least 80mm from bottom)
  const minYForTaxTable = pageHeight - 100;
  if (yPos < minYForTaxTable) {
    yPos = minYForTaxTable;
  }

  // ============================================
  // TAX SUMMARY TABLE
  // ============================================
  const taxTableColumns = ['TIPO', 'IMPORTE', 'DESCUENTO', 'PRONTO PAGO', '', 'BASE', 'I.V.A.'];
  const taxTableData = data.taxes.map(tax => [
    `${formatCurrency(tax.type)}`,
    formatCurrency(tax.base + tax.discount + tax.earlyPaymentDiscount),
    tax.discount > 0 ? formatCurrency(tax.discount) : '',
    tax.earlyPaymentDiscount > 0 ? formatCurrency(tax.earlyPaymentDiscount) : '',
    '',
    formatCurrency(tax.base),
    formatCurrency(tax.amount),
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [taxTableColumns],
    body: taxTableData,
    theme: 'plain',
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 8,
      halign: 'center',
      cellPadding: 2,
      lineColor: [200, 200, 200],
      lineWidth: 0.1,
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 3,
      halign: 'right',
      lineColor: [200, 200, 200],
      lineWidth: 0.1,
    },
    columnStyles: {
      0: { cellWidth: 20, halign: 'center' },
      1: { cellWidth: 28 },
      2: { cellWidth: 28 },
      3: { cellWidth: 28 },
      4: { cellWidth: 25 },
      5: { cellWidth: 28 },
      6: { cellWidth: 28 },
    },
    margin: { left: margin, right: margin },
    tableLineColor: [200, 200, 200],
    tableLineWidth: 0.1,
  });

  yPos = (doc as any).lastAutoTable.finalY + 5;

  // ============================================
  // OBSERVATIONS AND TOTAL
  // ============================================
  const obsWidth = (pageWidth - margin * 2) * 0.55;
  const totalWidth = (pageWidth - margin * 2) * 0.45;

  // Observations box
  doc.setFillColor(...MAGMA_RED);
  doc.rect(margin, yPos, obsWidth - 5, 6, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('Observaciones', margin + 3, yPos + 4);

  // Observations content box
  doc.setDrawColor(200, 200, 200);
  doc.rect(margin, yPos + 6, obsWidth - 5, 25);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(8);
  if (data.observations) {
    const obsLines = doc.splitTextToSize(data.observations, obsWidth - 10);
    doc.text(obsLines, margin + 3, yPos + 12);
  }

  // Total box
  const totalBoxX = margin + obsWidth;
  doc.setFillColor(...MAGMA_RED);
  doc.rect(totalBoxX, yPos, totalWidth - 5, 6, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('TOTAL EURO', totalBoxX + 3, yPos + 4.5);

  // Total amount
  if (!hideValues) {
    doc.setFillColor(...MAGMA_RED);
    doc.rect(totalBoxX + totalWidth - 45, yPos, 40, 6, 'F');
    doc.setFontSize(11);
    doc.text(formatCurrency(data.total), totalBoxX + totalWidth - 43, yPos + 4.5);
  }

  yPos += 40;

  // ============================================
  // LEGAL FOOTER
  // ============================================
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY_DARK);

  const legalText1 = 'En cumplimiento de lo dispuesto en el artículo 13 de Reglamento (UE) 2019/679 del Parlamento Europeo y del Consejo, le informamos que MAGMA COMPOSITES S.L. es el responsable del tratamiento y que dicho tratamiento se lleva a cabo con la finalidad de la gestión administrativa contable y posibles acciones comerciales, siendo su consentimiento nuestra base legal para ello.';
  const legalText2 = 'Asimismo, le informamos de la posibilidad que tiene de ejercer todos sus derechos sobre tratamiento de sus datos de caracter personal dirigiéndose por escrito al email info@magmacomposites.com';
  const legalText3 = 'Numero de productor de producto   ENV/2023/000016568';
  const legalText4 = 'Inscrita en el Reg. Mercantil de Barcelona, Tomo 35695, Hoja B276750';

  const footerY = pageHeight - 25;
  const textLines1 = doc.splitTextToSize(legalText1, pageWidth - margin * 2);
  doc.text(textLines1, margin, footerY);

  const textLines2 = doc.splitTextToSize(legalText2, pageWidth - margin * 2);
  doc.text(textLines2, margin, footerY + 8);

  doc.setFont('helvetica', 'normal');
  doc.text(legalText3, pageWidth / 2, footerY + 15, { align: 'center' });
  doc.text(legalText4, pageWidth / 2, footerY + 18, { align: 'center' });

  return doc;
}

export function downloadDeliveryNotePdf(data: DeliveryNote, filename?: string): void {
  const doc = generateDeliveryNotePdf(data);
  const suffix = data.hideValues ? '_Transporte' : '';
  const defaultFilename = `Albaran${suffix}_${data.documentNumber}_${data.customer.name.replace(/\s+/g, '_')}.pdf`;
  doc.save(filename || defaultFilename);
}

export function openDeliveryNotePdfInNewTab(data: DeliveryNote): void {
  const doc = generateDeliveryNotePdf(data);
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
}
